import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Table, Tag, Space, Statistic, Row, Col, Typography } from "antd";
import {
  previewGeneration,
  generateClassrooms,
} from "../../../../services/classrooms/classrooms.service";
import {
  addGroupToSchool,
  removeGroupFromSchool,
} from "../../../../services/schools/school.service";
import {
  CoordinatorInput,
  GenerationGrid,
  ProgramSubCell,
  CellStatus,
  SelectionCell,
} from "../../../../services/classrooms/classrooms.types";
import { useNotification } from "../../../../hooks/useNotification";
import { getApiErrorMessage } from "../../../../utils";

const { Text } = Typography;

interface GenerarClasesModalProps {
  open: boolean;
  onClose: () => void;
  schoolId: number;
  selections: SelectionCell[];
  coordinator?: CoordinatorInput;
  // Groups currently held in the grid's shared control — may include groups
  // not yet persisted to the school.
  groups: string[];
  // Groups already persisted to the school (as of the last load). Used to
  // compute which of `groups` are new and must be added additively before
  // generating — never removes an existing school group.
  existingGroups: string[];
  refreshClasses: () => Promise<void>;
  onGenerated: () => Promise<void> | void;
}

const STATUS_TAG: Record<
  CellStatus,
  { label: string; color: "green" | "default" | "blue" }
> = {
  new: { label: "Nueva", color: "green" },
  exists: { label: "Ya existe", color: "default" },
  reactivate: { label: "Reactivar", color: "blue" },
};

// Hardening backlog item 6: per-program status was colour-only, unreadable
// without colour vision. Distinct wording from the group's own Estado tag
// ("Nueva"/"Ya existe"/"Reactivar") so the two concepts never share the
// exact same on-screen text — "Nueva" (agreeing with "asignatura") is
// therefore off-limits here. Pending forms ("Por agregar"/"Por reactivar")
// throughout — this is a PREVIEW of work not yet done, and a past
// participle ("Reactivada") would misread as already completed.
const PROGRAM_STATUS_LABEL: Record<CellStatus, string> = {
  new: "Por agregar",
  exists: "Ya asignada",
  reactivate: "Por reactivar",
};

// classroom-group-model pivot (design #860 §7): the grid now emits 1 row per
// group, each carrying its own aggregate status plus a `programs[]` list —
// this column renders one chip per program, colored by (and now labeled
// with) that program's own join sub-status, independent of the group's own
// new/exists/reactivate state shown in "Estado".
const columns = [
  { title: "Grado", dataIndex: "grade_code", key: "grade_code" },
  { title: "Grupo", dataIndex: "custom_id", key: "custom_id" },
  { title: "Código", dataIndex: "custom_code", key: "custom_code" },
  {
    title: "Asignaturas",
    dataIndex: "programs",
    key: "programs",
    render: (programs: ProgramSubCell[]) => (
      <Space wrap size={4}>
        {programs.map((program) => (
          <Tag key={program.program_id} color={STATUS_TAG[program.status].color}>
            <span>{program.subject_name}</span>
            <span style={{ opacity: 0.75 }}>
              {" "}
              · {PROGRAM_STATUS_LABEL[program.status]}
            </span>
          </Tag>
        ))}
      </Space>
    ),
  },
  {
    title: "Estado",
    dataIndex: "status",
    key: "status",
    render: (status: CellStatus) => {
      const tag = STATUS_TAG[status];
      return <Tag color={tag.color}>{tag.label}</Tag>;
    },
  },
];

/**
 * "Generar clases" modal, opened from Clases: previews the backend-resolved
 * grid before allowing generate (spec "Confirmation blocks direct
 * generation"). On confirm, first persists any NEW shared groups additively
 * (mirrors GroupsTab's addGroupToSchool — never uses updateSchool's groups
 * array, which would remove existing school groups not present in the
 * payload), then generates.
 */
const GenerarClasesModal: React.FC<GenerarClasesModalProps> = ({
  open,
  onClose,
  schoolId,
  selections,
  coordinator,
  groups,
  existingGroups,
  refreshClasses,
  onGenerated,
}) => {
  const { notify } = useNotification();
  const [loadingPreview, setLoadingPreview] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [grid, setGrid] = useState<GenerationGrid | null>(null);

  // The summary Statistics above count GROUPS (grid.summary.new/exists/
  // reactivate), never program joins — a group already `exists` can still
  // gain a brand-new (or reactivated) program join (hardening backlog item
  // 6), which the group-level counters alone would hide entirely (e.g. "0
  // nuevas" while N program joins are about to be created or reactivated).
  const pendingProgramJoins = useMemo(
    () =>
      (grid?.cells ?? []).reduce(
        (count, cell) =>
          count +
          cell.programs.filter(
            (p) => p.status === "new" || p.status === "reactivate"
          ).length,
        0
      ),
    [grid]
  );

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const result = await previewGeneration(schoolId, { selections, coordinator });
      setGrid(result);
    } catch (error) {
      console.error("Error previewing generation:", error);
      notify(
        "error",
        "Error",
        getApiErrorMessage(
          error,
          "No se pudo previsualizar la generación de clases."
        )
      );
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setGrid(null);
    loadPreview();
    // Only re-preview when the modal opens or the school/selection identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, schoolId, selections]);

  // `created` accumulates the names actually persisted, so a throw partway
  // through still leaves the caller with the exact list to compensate.
  const persistNewGroups = async (created: string[]) => {
    const existingLower = existingGroups.map((g) => g.trim().toLowerCase());
    // Blank names (a group-name input mid-edit) are never persisted.
    const newGroups = groups
      .map((g) => g.trim())
      .filter((g) => g !== "" && !existingLower.includes(g.toLowerCase()));
    for (const groupName of newGroups) {
      // addGroupToSchool resolves null on a non-201 — treat that as a hard
      // failure. Generating classes for a group that was never persisted
      // would leave classrooms referencing a group the school does not have.
      const persisted = await addGroupToSchool(schoolId, groupName);
      if (!persisted) {
        throw new Error(`Group "${groupName}" could not be persisted`);
      }
      created.push(groupName);
    }
  };

  // Compensation for the persist-then-generate order below: the backend rolls
  // back the CLASS transaction only, so a failed generation used to leave the
  // groups this call had just created in the school catalogue with zero
  // classes (SCRUM-915). Never throws — the original failure is what the
  // operator must see.
  const removeCreatedGroups = async (created: string[]) => {
    for (const groupName of created) {
      try {
        const removed = await removeGroupFromSchool(schoolId, groupName);
        if (!removed) {
          console.error(
            `Rollback failed: group "${groupName}" is still in the school catalogue`
          );
        }
      } catch (cleanupError) {
        console.error(
          `Rollback failed: group "${groupName}" is still in the school catalogue`,
          cleanupError
        );
      }
    }
  };

  // Synchronous re-entrancy guard: `generating` state lags a render behind,
  // so a fast double-click would fire two full persist+generate chains.
  const generateInFlightRef = useRef(false);

  const handleGenerate = async () => {
    if (!grid) return;
    if (generateInFlightRef.current) return;
    generateInFlightRef.current = true;
    setGenerating(true);
    try {
      const createdGroups: string[] = [];
      try {
        // Additive-only, never via updateSchool's groups array (which would
        // remove existing school groups not present in this payload).
        await persistNewGroups(createdGroups);
        await generateClassrooms(schoolId, { selections, coordinator });
      } catch (error) {
        await removeCreatedGroups(createdGroups);
        throw error;
      }
      // Point of no return: everything below is post-success reporting, and a
      // failure there must never roll back the groups just generated against.
      notify("success", "Éxito", "Clases generadas correctamente.");

      // Generation itself succeeded — a refresh failure here is a distinct,
      // lower-severity problem, not a false success/failure signal.
      try {
        await refreshClasses();
      } catch (refreshError) {
        console.error("Error refreshing after generation:", refreshError);
        notify(
          "warning",
          "Atención",
          "Clases generadas, pero no se pudo refrescar la lista — recargá la página."
        );
      }

      // Same isolation for the post-generation reload: generation already
      // succeeded, so a reload failure must not surface as a generation
      // error (and must not keep the modal open for a redundant retry).
      try {
        await onGenerated();
      } catch (reloadError) {
        console.error("Error reloading after generation:", reloadError);
        notify(
          "warning",
          "Atención",
          "Clases generadas, pero no se pudo actualizar la vista — recargá la página."
        );
      }
      onClose();
    } catch (error) {
      console.error("Error generating classrooms:", error);
      // The backend's 400s here are actionable (correo del coordinador en
      // uso, grupos duplicados, códigos en colisión) — show them.
      notify(
        "error",
        "Error",
        getApiErrorMessage(error, "Ocurrió un error al generar las clases.")
      );
    } finally {
      generateInFlightRef.current = false;
      setGenerating(false);
    }
  };

  return (
    <Modal
      title="Confirmación de Clases"
      open={open}
      onCancel={onClose}
      footer={null}
      width={800}
      destroyOnClose
      // Dismissal hardening: while a generation is in flight, the modal
      // must not be closable via the X icon, mask click, or Escape. Closing
      // mid-flight let a stale existingProgramIds/groups snapshot be sent
      // by a subsequent "Actualizar Escuela" before loadSchool() caught up,
      // silently cascade-deleting the classrooms/groups just generated.
      closable={!generating}
      maskClosable={!generating}
      keyboard={!generating}
    >
      {grid && (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Statistic title="Total" value={grid.summary.total} />
          </Col>
          <Col span={6}>
            <Statistic title="Nuevas" value={grid.summary.new} />
          </Col>
          <Col span={6}>
            <Statistic title="Ya existentes" value={grid.summary.exists} />
          </Col>
          <Col span={6}>
            <Statistic title="A reactivar" value={grid.summary.reactivate} />
          </Col>
          {pendingProgramJoins > 0 && (
            <Col span={24} style={{ marginTop: 8 }}>
              <Text type="secondary">
                Incluye {pendingProgramJoins} vínculo(s) de asignatura
                pendiente(s) (nuevo o a reactivar) que se asociarán a grupos
                (nuevos o ya existentes).
              </Text>
            </Col>
          )}
        </Row>
      )}

      <Table
        columns={columns}
        dataSource={grid?.cells ?? []}
        rowKey="custom_code"
        loading={loadingPreview}
        pagination={false}
      />

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" onClick={handleGenerate} disabled={!grid} loading={generating}>
          Confirmar y generar
        </Button>
      </Space>
    </Modal>
  );
};

export default GenerarClasesModal;
